// src/components/hero/HeroContent.tsx
import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { cn, isInAppWebView } from '@/lib/utils';
import { LogIn } from 'lucide-react';

interface HeroContentProps {
  onSignIn: () => void;
  onRequestAccess: () => void;
  inviteCodeSignup?: React.ReactNode;
}

export const HeroContent: React.FC<HeroContentProps> = ({
  onSignIn,
  onRequestAccess,
  inviteCodeSignup
}) => {
  return (
    <motion.section 
      className={cn(
        "max-w-4xl mx-auto text-center",
        "px-4 sm:px-6 lg:px-8",
        "mt-12 sm:mt-24 lg:mt-32",
        "mb-24 sm:mb-32"
      )}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.h1 
        className={cn(
          "font-sans font-bold mb-8",
          "text-5xl sm:text-6xl md:text-7xl lg:text-8xl",
          "bg-clip-text text-transparent",
          "bg-gradient-to-r from-white via-amber-200 to-amber-400",
          "tracking-tight leading-tight"
        )}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          delay: 0.2,
          duration: 0.8,
          ease: [0.4, 0, 0.2, 1]
        }}
      >
        Play the World.
      </motion.h1>
      
      <motion.p 
        className={cn(
          "font-sans font-light mb-16",
          "text-xl sm:text-2xl md:text-3xl",
          "text-gray-300",
          "max-w-2xl mx-auto mb-4 sm:mb-8",
          "leading-relaxed px-4"
        )}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          delay: 0.4,
          duration: 0.8,
          ease: [0.4, 0, 0.2, 1]
        }}
      >
      <b> Connect with private club members globally through sports & wellness.</b>
      </motion.p>
      
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ 
          delay: 0.6,
          duration: 0.8,
          ease: [0.4, 0, 0.2, 1]
        }}
      >
        <div className="flex flex-col items-center space-y-6">          
          <Button
            onClick={onSignIn}
            className={cn(
              "relative overflow-hidden group",
              "bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500",
              "text-dark-50 font-light",
              "h-12 sm:h-14 md:h-16 px-6 sm:px-8 md:px-12",
              "rounded-full",
              "text-base sm:text-lg md:text-xl tracking-wide", 
              "transform transition-all duration-500 hover:scale-105",
              "shadow-[0_0_15px_rgba(251,191,36,0.3)] hover:shadow-[0_0_35px_rgba(251,191,36,0.5)]",
              "border border-amber-400/20",
              "flex items-center justify-center gap-3"
            )}
          >
            <span className="relative z-10 font-medium text-sm sm:text-base md:text-lg">Existing Member Sign In</span>
            <LogIn className="relative z-10 w-5 h-5 sm:w-6 sm:h-6" />
            <div 
              className={cn(
                "absolute inset-0",
                "bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-500",
                "opacity-0 group-hover:opacity-100",
                "transition-all duration-500",
                "blur-[0.5px]"
              )} 
            />
            <div 
              className={cn(
                "absolute inset-0 -translate-x-full",
                "bg-gradient-to-r from-transparent via-white/20 to-transparent", 
                "group-hover:translate-x-full",
                "transition-transform duration-1000 ease-in-out",
                "z-20"
              )}
            />
          </Button>          

          {/* App Store and Play Store badges shown only on web */}
          <div className="space-y-6 mt-4">
            {/* Invite Code Signup - Moved above app store buttons for immediate visibility */}
            {inviteCodeSignup}
            
            {!isInAppWebView() && (
              <div className="flex flex-row items-center justify-center space-x-4">
                <a
                  href="https://apps.apple.com/us/app/playclub/id6743469207"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:opacity-90 transition-opacity transform hover:scale-105"
                >
                  <img
                    src="https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/en-us?releaseDate=1743465600"
                    alt="Download on the App Store"
                    className="w-[140px] object-contain"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.playclub.playclubandroid"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block hover:opacity-90 transition-opacity transform hover:scale-105"
                >
                  <img
                    src="https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png"
                    alt="Get it on Google Play"
                    className="w-[160px] h-auto object-contain"
                  />
                </a>
              </div>
            )}
            
            {/* Apply for Membership text moved below app store buttons */}
            <div className="text-gray-400 font-sans mt-4">
              Not a member yet?{' '}
              <button
                onClick={onRequestAccess}
                className={cn(
                  "text-amber-400 hover:text-amber-300",
                  "transition-colors duration-200",
                  "font-medium"
                )}
              >
                Apply for Membership
              </button>
            </div>
          </div>
          
        </div>
      </motion.div>
    </motion.section>
  );
};
